import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/Api';
import { api_url_v1 } from '../utils/Config';

const initialState = {
  items: [],
  selectedAdmin: null,
  meta: {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  },
  loading: false,
  error: null
};

export const fetchAdmins = createAsyncThunk(
  'admins/fetchAdmins',
  async ({ page, limit, filter, filter_type, search }, { getState }) => {
    const { auth: { token } } = getState();
    const params = {
      page,
      limit,
      [`filter[${filter_type}]`]: `${filter}`,
      search
    }
    const response = await api.get(`${api_url_v1}/all`, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    return {
      data: response.data.data || [],
      meta: response.data.meta || {
        current_page: page,
        last_page: 1,
        per_page: limit,
        total: 0
      }
    };
  }
);
export const createAdmin = createAsyncThunk(
    'admins/createAdmin',
    async (adminData, { getState, rejectWithValue }) => {
      try {
        const { auth: { token } } = getState();
        const response = await api.post(`${api_url_v1}/store`, adminData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        return response.data.data; 
      } catch (error) {
        return rejectWithValue(error.response?.data || 'Admin creation failed');
      }
    }
  );

export const fetchAdminDetails = createAsyncThunk(
  'admins/fetchAdminDetails',
  async (slug, { getState }) => {
    const { auth: { token } } = getState();
    const response = await api.get(`${api_url_v1}/${slug}/show`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  }
);

export const updateAdminProfile = createAsyncThunk(
  'admins/updateAdminProfile',
  async ({ adminId, data }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.put(`${api_url_v1}/${adminId}/update`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return { adminId, updatedAdmin: response.data.data }; 
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Update failed');
    }
  }
);

const adminSlice = createSlice({
  name: 'adminsSlice',
  initialState,
  reducers: {
    setSelectedAdmin: (state, action) => {
      state.selectedAdmin = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Admins Cases
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch Admin Details Cases
      .addCase(fetchAdminDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAdmin = action.payload.data;
      })
      .addCase(fetchAdminDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Update Admin Profile Cases
      .addCase(updateAdminProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminProfile.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update the admin in the items array
        const index = state.items.findIndex(admin => admin.id === action.payload.adminId);
        if (index !== -1) {
          state.items[index] = action.payload.updatedAdmin;
        }
        
        // Update selectedAdmin if it's the same admin
        if (state.selectedAdmin?.id === action.payload.adminId) {
          state.selectedAdmin = action.payload.updatedAdmin;
        }
      })
      .addCase(updateAdminProfile.rejected, (state, action) => {
        state.loading = true;
        state.error = action.payload || 'Update failed';
      })
      .addCase(createAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new admin to the items array
        state.items.push(action.payload);
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Admin creation failed';
      });
  }
});

export const { setSelectedAdmin } = adminSlice.actions;
export default adminSlice.reducer;